import React, { useEffect, useState } from "react";
import Commonmodal from "./Commonmodal";
import toast from "react-hot-toast";

const RefundChecklistModal = ({
  open,
  onClose,
  onConfirm,
  bookingId,
  costToCustomer,
  totalAmountPaid,
  bookingCode,
  chargeGroups,
  manualChecks,
  derivedChecks,
  bookingComments = [],
  fleetBookingComments = [],
  readOnly = false,
}) => {
  const cost = Number(costToCustomer) || 0;
  const paid = Number(totalAmountPaid) || 0;
  const pending = cost - paid;
  const isFullyPaid = pending <= 0;

  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  // Logged-in agent details — read-only, sourced from the "profile" key in localStorage
  const loggedInUser = (() => {
    try {
      const raw = localStorage.getItem("profile");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const agentName = loggedInUser?.name || "Unknown Agent";
  const agentEmail = loggedInUser?.email || "";

  const [recordId, setRecordId] = useState(null);
  // const [reviewerName, setReviewerName] = useState("");
  // const [comments, setComments] = useState("");
  const [checkedStates, setCheckedStates] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const [odometerStart, setOdometerStart] = useState(null);
  const [odometerEnd, setOdometerEnd] = useState(null);
  const [repairBillImages, setRepairBillImages] = useState([]);
  const [tollReceiptImages, setTollReceiptImages] = useState([]);
  const [fuelBillImages, setFuelBillImages] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);

  const [openImage, setOpenImage] = useState(false);
  const [imageSelection, setImageSelection] = useState(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isFleetAccordionOpen, setIsFleetAccordionOpen] = useState(false);
  const [checklistComments, setChecklistComments] = useState({});
  const [verifiedAt, setVerifiedAt] = useState(null);
  // Details of whoever actually submitted this record — resolved via /api/users using
  // the server-derived createdBy id (NOT the current viewer's localStorage profile)
  const [savedAgentName, setSavedAgentName] = useState(null);
  const [savedAgentEmail, setSavedAgentEmail] = useState(null);

  const customerTripImageCategory = JSON.parse(
      localStorage.getItem("catalogValues")
    )?.customerTripImageCategory;
    //delete customerTripImageCategory?.inverted;

  const handleCommentChange = (id, value) => {
    if (readOnly) return;
    setChecklistComments((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Fetch odometer, images, and saved draft/verification progress
  useEffect(() => {
    if (!open || !bookingId) return;

    const fetchModalData = async () => {
      setLoadingData(true);
      try {
        const [startRes, endRes, imagesRes, verificationRes, usersRes] = await Promise.all([
          fetch(`${apiUrl}/api/tripstartdetails?bookingId=${bookingId}`).then((res) => res.json()),
          fetch(`${apiUrl}/api/tripenddetails?bookingId=${bookingId}`).then((res) => res.json()),
          fetch(`${apiUrl}/api/bookingimages?bookingId=${bookingId}`).then((res) => res.json()),
          fetch(`${apiUrl}/api/refundchecklistverifications?bookingId=${bookingId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `${token}`,
            },
          }).then((res) => res.json()),
          fetch(`${apiUrl}/api/users?limit=1000&isSystemUser=1`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `${token}`,
            },
          }).then((res) => res.json()),
        ]);

        // Map of userId -> { name, email }, used to resolve who actually submitted the checklist
        const usersById = {};
        (usersRes?.entities?.rows || []).forEach((u) => {
          usersById[u.id] = { name: u.name, email: u.email };
        });

        if (startRes?.entities?.rows?.length > 0) setOdometerStart(startRes.entities.rows[0]);
        if (endRes?.entities?.rows?.length > 0) setOdometerEnd(endRes.entities.rows[0]);

        if (imagesRes?.entities?.rows?.length > 0) {
          const parseImages = (rows, subCatId) =>
            rows
          .filter((img) => String(img.subCategoryId) === String(subCatId))
          .flatMap((img) => {
            try {
                  return typeof img.images === "string" ? JSON.parse(img.images) : img.images;
                } catch (err) {
                  return [];
                }
              });
          setTollReceiptImages(parseImages(imagesRes.entities.rows, customerTripImageCategory?.inverted?.tollReceipt));
          setFuelBillImages(parseImages(imagesRes.entities.rows, customerTripImageCategory?.inverted?.fuelBills));
          setRepairBillImages(parseImages(imagesRes.entities.rows, customerTripImageCategory?.inverted?.repairBills));
        }

        // Restore saved draft/completed verification data
        // Restore saved draft/completed verification data
        if (verificationRes?.statusCode === "0100") {
          const rows =
            verificationRes?.entities?.rows ||
            (verificationRes?.dataValues ? [verificationRes.dataValues] : []);

          if (rows.length > 0) {
            // Sort descending to pick the latest record
            const sortedRows = [...rows].sort((a, b) => (b.id || 0) - (a.id || 0));
            const existingData = sortedRows[0];

            setRecordId(existingData.id || null);
            if (existingData.createdAt) setVerifiedAt(existingData.createdAt);
            const submitter = usersById[existingData.createdBy];
            if (submitter) {
              setSavedAgentName(submitter.name);
              setSavedAgentEmail(submitter.email);
            }

            if (existingData.listValues) {
              try {
                const parsed =
                  typeof existingData.listValues === "string"
                    ? JSON.parse(existingData.listValues.replace(/'/g, '"'))
                    : existingData.listValues;

                // // 1. Restore embedded reviewerName and comments
                // if (parsed?.reviewerName !== undefined) {
                //   setReviewerName(parsed.reviewerName);
                // }
                // if (parsed?.comments !== undefined) {
                //   setComments(parsed.comments);
                // }

                // 2. Extract items array (handles new object format & fallback to old array format)
                const itemsArray = Array.isArray(parsed) ? parsed : parsed?.items || [];

                const restoredStates = {};

                manualChecks?.forEach((check) => {
                  restoredStates[check.id] = readOnly ? true : false;
                });
                derivedChecks?.forEach((check) => {
                  restoredStates[check.id] = readOnly ? true : check.isChecked;
                });

                if (Array.isArray(itemsArray)) {
  const restoredComments = {};
  itemsArray.forEach((item) => {
    if (item?.id) {
      restoredStates[item.id] = !!item.status;
      if (item.comment) {
        restoredComments[item.id] = item.comment; // Restore saved comment
      }
    }
  });
  setCheckedStates(restoredStates);
  setChecklistComments(restoredComments); // Set state
}

                setCheckedStates(restoredStates);
              } catch (error) {
                console.error("Error parsing saved listValues JSON:", error);
              }
            }
          } else {
            // Default initial checkbox states if no record exists
            const initialStates = {};
            manualChecks?.forEach((check) => {
              initialStates[check.id] = readOnly ? true : false;
            });
            derivedChecks?.forEach((check) => {
              initialStates[check.id] = readOnly ? true : check.isChecked;
            });
            setCheckedStates(initialStates);
          }
        }
      } catch (error) {
        console.error("Error fetching checklist data", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchModalData();
  }, [open, bookingId, apiUrl, readOnly, token]);

  const handleCheckboxChange = (id) => {
    if (readOnly) return;
    setCheckedStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleSelectAll = () => {
  if (readOnly) return;
  
  const newSelectAllState = !selectAll;
  setSelectAll(newSelectAllState);
  
  const allCheckIds = [
    ...(manualChecks?.map(check => check.id) || []),
    ...(derivedChecks?.map(check => check.id) || [])
  ];
  
  const newCheckedStates = {};
  allCheckIds.forEach(id => {
    newCheckedStates[id] = newSelectAllState;
  });
  
  setCheckedStates(prev => ({
    ...prev,
    ...newCheckedStates
  }));
};

  const handleSaveChecklist = async (status) => {
    // if (!reviewerName.trim() && status === 1) {
    //   toast.error("Reviewer name is mandatory.");
    //   return;
    // }

    setSaving(true);

    const submissionTimestamp = new Date().toISOString();

    const listValuesArray = Object.entries(checkedStates).map(([id, isChecked]) => ({
      id,
      status: isChecked,
      comment: checklistComments[id] || "",
      createdBy: loggedInUser?.id || 1,
    }));

    const body = {
      id: recordId ? Number(recordId) : null,
      bookingId: Number(bookingId),
      // reviewerName,
      // comments,
      listValues: JSON.stringify(listValuesArray),
      status: status, // 0 for DRAFT, 1 for COMPLETED
      // Note: agent identity and submission time are NOT sent here — the backend already
      // derives createdBy/createdAt server-side from the auth token, which is what the
      // "Verified By" display resolves against (see fetchModalData). Sending our own
      // agentName/verifiedAt would be redundant and the API schema drops them anyway.
    };

    try {
      const response = await fetch(`${apiUrl}/api/refundchecklistverifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data?.statusCode === "0100" || response.ok) {
        toast.success(status === 0 ? "Draft saved successfully!" : "Verification completed!");
        
        const newId = data?.dataValues?.id || data?.entityValue?.id || data?.id;
        if (newId) setRecordId(newId);

        // Reflect the submission immediately without waiting for a refetch.
        // Prefer the server's own createdAt/createdBy if it echoed them back; otherwise
        // fall back to what we know locally (current user, current time).
        const savedRow = data?.dataValues || data?.entityValue || data;
        setVerifiedAt(savedRow?.createdAt || submissionTimestamp);
        setSavedAgentName(loggedInUser?.name || null);
        setSavedAgentEmail(loggedInUser?.email || null);

        if (status === 1 && onConfirm) {
          onConfirm();
        }
      } else {
        toast.error(data?.msg || "Failed to save checklist.");
      }
    } catch (error) {
      console.error("Error saving checklist:", error);
      toast.error("Error saving checklist progress.");
    } finally {
      setSaving(false);
    }
  };

  const handleImageClose = () => {
    setImageSelection(null);
    setOpenImage(false);
  };

  const handleImageOpen = (url) => {
    if (!url) return;
    setImageSelection(url);
    setOpenImage(true);
  };

  const isConfirmEnabled = () => {
    if (readOnly) return false;
    // if (!reviewerName.trim()) return false;
    const totalChecks = (manualChecks?.length || 0) + (derivedChecks?.length || 0);
    if (totalChecks === 0) return true;
    return Object.values(checkedStates).every((val) => val === true);
  };

  if (!open) return null;

  return (
    <>
      {openImage && <Commonmodal open={openImage} onClose={handleImageClose} url={imageSelection} />}
      
      {/* Background Overlay (Click to close) */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 sm:p-6"
      >
        {/* Modal Wrapper (Adjustable Viewport Dimensions) */}
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-[95vw] h-[90vh] max-w-6xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center shrink-0">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {readOnly ? "View Refund Checklist Details" : "Pre-Refund Verification Checklist"}
              </h2>
              <p className="text-xs font-semibold text-blue-600 mt-0.5">
                Booking ID: {bookingCode ? `${bookingCode}` : ""}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-red-500 font-bold text-xl">✕</button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1 space-y-8">
            
            {/* Reviewer Details */}
            {/* <section className="bg-blue-50/60 p-5 rounded-xl border border-blue-100 space-y-4">
              <h3 className="text-md font-bold text-gray-800 border-b border-blue-200 pb-2">
                Verification Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                    Reviewer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    disabled={readOnly}
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    placeholder="Enter reviewer name..."
                    className="w-full border-2 border-gray-200 rounded-lg p-2.5 text-sm focus:ring focus:ring-blue-100 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                    Checklist Comments <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <textarea
                    rows={2}
                    disabled={readOnly}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Add optional notes or observations..."
                    className="w-full border-2 border-gray-200 rounded-lg p-2.5 text-sm resize-none focus:ring focus:ring-blue-100 focus:border-blue-500"
                  />
                </div>
              </div>
            </section> */}

            {/* Verified Agent Details — auto-filled, uneditable */}
            <section className="bg-blue-50/60 p-5 rounded-xl border border-blue-100">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider border-b border-blue-200 pb-3 mb-4">
                Verified By
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                    Agent Name
                  </label>
                  <div className="w-full border-2 border-gray-200 bg-gray-100 rounded-lg p-2.5 text-sm text-gray-700 cursor-not-allowed truncate">
                    {savedAgentName || agentName}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                    Email
                  </label>
                  <div className="w-full border-2 border-gray-200 bg-gray-100 rounded-lg p-2.5 text-sm text-gray-700 cursor-not-allowed truncate">
                    {savedAgentEmail || agentEmail || "—"}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                    Date &amp; Time
                  </label>
                  <div className="w-full border-2 border-gray-200 bg-gray-100 rounded-lg p-2.5 text-sm text-gray-700 cursor-not-allowed truncate">
                    {verifiedAt
                      ? new Date(verifiedAt).toLocaleString()
                      : readOnly
                      ? "—"
                      : "Captured on submission"}
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-gray-50 p-5 rounded-xl border border-gray-200">
      <div className="flex justify-between items-center border-b border-gray-200 pb-3 mb-4">
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
          Financial Summary
        </h3>
        <span
          className={`text-xs font-semibold px-3 py-1 rounded-full ${
            isFullyPaid
              ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
              : "bg-amber-100 text-amber-800 border border-amber-300"
          }`}
        >
          {isFullyPaid ? "Fully Paid" : "Pending Payment"}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Cost to Customer */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Cost to Customer
          </span>
          <span className="text-lg font-bold text-gray-900 mt-1">
            ₹{cost.toFixed(2)}
          </span>
        </div>

        {/* Total Amount Paid */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Total Amount Paid
          </span>
          <span className="text-lg font-bold text-emerald-600 mt-1">
            ₹{paid.toFixed(2)}
          </span>
        </div>

        {/* Pending Amount */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-between">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Pending Amount
          </span>
          <span
            className={`text-lg font-bold mt-1 ${
              pending > 0 ? "text-amber-600" : "text-gray-500"
            }`}
          >
            ₹{(pending > 0 ? pending : 0).toFixed(2)}
          </span>
        </div>
      </div>
    </section>
            

            {/* Accordion: Booking Comments Log */}
            <section className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                className="w-full px-5 py-3.5 bg-gray-100 flex justify-between items-center text-left hover:bg-gray-200 transition"
              >
                <div className="flex items-center gap-2 font-bold text-gray-800 text-sm">
                  <span>Booking Comments</span>
                  <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {bookingComments?.length || 0}
                  </span>
                </div>
                <span className="text-gray-500 text-sm font-bold">{isAccordionOpen ? "▲ Hide" : "▼ Show"}</span>
              </button>

              {isAccordionOpen && (
                <div className="p-4 max-h-60 overflow-y-auto space-y-3 bg-gray-50 border-t border-gray-200">
                  {bookingComments?.length > 0 ? (
                    bookingComments.map((comment, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border border-gray-200 text-xs shadow-sm">
                        <div className="flex justify-between items-center text-gray-500 mb-1">
                          <span className="font-semibold text-gray-700">{comment?.createdBy || "User"}</span>
                          <span>{comment?.createdAt ? new Date(comment.createdAt).toLocaleString() : ""}</span>
                        </div>
                        <p className="text-gray-800 font-medium">{comment?.comments || comment?.comment || "-"}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-2">No booking comments recorded.</p>
                  )}
                </div>
              )}
            </section>

            {/* Accordion: Fleet Booking Comments */}
            <section className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setIsFleetAccordionOpen(!isFleetAccordionOpen)}
                className="w-full px-5 py-3.5 bg-gray-100 flex justify-between items-center text-left hover:bg-gray-200 transition"
              >
                <div className="flex items-center gap-2 font-bold text-gray-800 text-sm">
                  <span>Fleet Booking Comments</span>
                  <span className="bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {fleetBookingComments?.length || 0}
                  </span>
                </div>
                <span className="text-gray-500 text-sm font-bold">{isFleetAccordionOpen ? "▲ Hide" : "▼ Show"}</span>
              </button>

              {isFleetAccordionOpen && (
                <div className="p-4 max-h-60 overflow-y-auto space-y-3 bg-gray-50 border-t border-gray-200">
                  {fleetBookingComments?.length > 0 ? (
                    fleetBookingComments.map((comment, index) => (
                      <div key={index} className="bg-white p-3 rounded-lg border border-gray-200 text-xs shadow-sm">
                        <div className="flex justify-between items-center text-gray-500 mb-1">
                          <span className="font-semibold text-gray-700">{comment?.createdBy || "Fleet User"}</span>
                          <span>{comment?.createdAt ? new Date(comment.createdAt).toLocaleString() : ""}</span>
                        </div>
                        <p className="text-gray-800 font-medium">{comment?.comments || comment?.comment || "-"}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-2">No fleet booking comments recorded.</p>
                  )}
                </div>
              )}
            </section>

            {/* Odometer & Fuel Section with Trip Comments */}
<section>
  <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">
    Vehicle State Verification
  </h3>
  {loadingData ? (
    <p className="text-gray-500">Loading details...</p>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Start Odometer & Comments */}
      <div className="bg-gray-50 p-4 rounded-xl border flex flex-col justify-between">
        <div className="flex flex-col items-center">
          <p className="font-semibold text-gray-700 mb-1">
            Start Odometer: {odometerStart?.startingOdometer ?? "N/A"} km
          </p>
          <p className="font-semibold text-blue-600 mb-3">
            Starting Fuel: {odometerStart?.startingFuelVolume ?? "N/A"}%
          </p>
          {odometerStart?.odometerImage ? (
            <img
              src={odometerStart.odometerImage}
              alt="Start Odometer"
              onClick={() => handleImageOpen(odometerStart.odometerImage)}
              className="h-32 object-contain cursor-pointer rounded border shadow-sm hover:opacity-80 transition"
            />
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-400 bg-gray-200 w-full rounded border-dashed border-2">
              Not uploaded
            </div>
          )}
        </div>

        {/* Trip Start Comment Card */}
        <div className="flex items-center justify-center mt-4 pt-3 border-t border-gray-200 w-full">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Trip Start Comment
          </p>
          <div className="bg-white p-2.5 rounded-lg border border-gray-200 text-xs text-gray-800 min-h-[40px]">
            {odometerStart?.remarks || odometerStart?.comment || odometerStart?.comments || (
              <span className="text-gray-400 italic">No start comments added.</span>
            )}
          </div>
        </div>
      </div>

      {/* End Odometer & Comments */}
      <div className="bg-gray-50 p-4 rounded-xl border flex flex-col justify-between">
        <div className="flex flex-col items-center">
          <p className="font-semibold text-gray-700 mb-1">
            End Odometer: {odometerEnd?.endingOdometer ?? "N/A"} km
          </p>
          <p className="font-semibold text-blue-600 mb-3">
            Ending Fuel: {odometerEnd?.endingFuelVolume ?? "N/A"}%
          </p>
          {odometerEnd?.odometerImage ? (
            <img
              src={odometerEnd.odometerImage}
              alt="End Odometer"
              onClick={() => handleImageOpen(odometerEnd.odometerImage)}
              className="h-32 object-contain cursor-pointer rounded border shadow-sm hover:opacity-80 transition"
            />
          ) : (
            <div className="h-32 flex items-center justify-center text-gray-400 bg-gray-200 w-full rounded border-dashed border-2">
              Not uploaded
            </div>
          )}
        </div>

        {/* Trip End Comment Card */}
        <div className=" flex items-center justify-center mt-4 pt-3 border-t border-gray-200 w-full">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
            Trip End Comment
          </p>
          <div className="bg-white p-2.5 rounded-lg border border-gray-200 text-xs text-gray-800 min-h-[40px]">
            {odometerEnd?.remarks || odometerEnd?.comment || odometerEnd?.comments || (
              <span className="text-gray-400 italic">No end comments added.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  )}
</section>

            {/* Receipts & Bills (Toll 654, Fuel 655, Repair 656) */}
            <section className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Trip Receipts & Bills</h3>
              
              {loadingData ? (
                <p className="text-gray-500 text-sm">Loading receipt images...</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Toll Receipts */}
                  <div className="bg-blue-50/50 p-4 rounded-xl border">
                    <h4 className="font-semibold text-blue-900 text-sm mb-3">Toll Receipts </h4>
                    {tollReceiptImages.length > 0 ? (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {tollReceiptImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.url}
                            alt="Toll Receipt"
                            onClick={() => handleImageOpen(img.url)}
                            className="h-20 w-20 object-cover rounded border cursor-pointer hover:shadow"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No toll receipts uploaded.</p>
                    )}
                  </div>

                  {/* Fuel Bills */}
                  <div className="bg-orange-50/50 p-4 rounded-xl border">
                    <h4 className="font-semibold text-orange-900 text-sm mb-3">Fuel Bills </h4>
                    {fuelBillImages.length > 0 ? (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {fuelBillImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.url}
                            alt="Fuel Bill"
                            onClick={() => handleImageOpen(img.url)}
                            className="h-20 w-20 object-cover rounded border cursor-pointer hover:shadow"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No fuel bills uploaded.</p>
                    )}
                  </div>

                  {/* Repair Bills */}
                  <div className="bg-gray-50 p-4 rounded-xl border">
                    <h4 className="font-semibold text-gray-900 text-sm mb-3">Repair Bills </h4>
                    {repairBillImages.length > 0 ? (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {repairBillImages.map((img, idx) => (
                          <img
                            key={idx}
                            src={img.url}
                            alt="Repair Bill"
                            onClick={() => handleImageOpen(img.url)}
                            className="h-20 w-20 object-cover rounded border cursor-pointer hover:shadow"
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No repair bills uploaded.</p>
                    )}
                  </div>
                </div>
              )}
            </section>

            {/* Charge Groups */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <h3 className="text-sm font-bold text-blue-800 mb-3">FASTag Charges</h3>
                {chargeGroups?.fastag?.length > 0 ? (
                  <div className="flex gap-2 flex-wrap">
                    {chargeGroups.fastag.map((charge) => (
                      <div key={charge.id} className="relative group shrink-0">
                         {charge.imageUrl ? (
                           <img
                             src={charge.imageUrl}
                             alt="FASTag"
                             onClick={() => handleImageOpen(charge.imageUrl)}
                             className="h-14 w-14 object-cover rounded border border-gray-300 cursor-pointer hover:shadow-md"
                           />
                         ) : (
                           <div className="h-14 w-14 bg-gray-200 text-[10px] flex items-center justify-center text-center text-gray-500 rounded border">No Img</div>
                         )}
                         <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow">
                           ₹{charge.amount}
                         </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No FASTag charges.</p>
                )}
              </div>

              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <h3 className="text-sm font-bold text-orange-800 mb-3">Fuel Charges</h3>
                {chargeGroups?.fuel?.length > 0 ? (
                  <div className="flex gap-2 flex-wrap">
                    {chargeGroups.fuel.map((charge) => (
                      <div key={charge.id} className="relative group shrink-0">
                         {charge.imageUrl ? (
                           <img
                             src={charge.imageUrl}
                             alt="Fuel"
                             onClick={() => handleImageOpen(charge.imageUrl)}
                             className="h-14 w-14 object-cover rounded border border-gray-300 cursor-pointer hover:shadow-md"
                           />
                         ) : (
                           <div className="h-14 w-14 bg-gray-200 text-[10px] flex items-center justify-center text-center text-gray-500 rounded border">No Img</div>
                         )}
                         <div className="absolute -top-2 -right-2 bg-orange-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow">
                           ₹{charge.amount}
                         </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No Fuel charges.</p>
                )}
              </div>

              <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                <h3 className="text-sm font-bold text-red-800 mb-3">Traffic Violations</h3>
                {chargeGroups?.traffic?.length > 0 ? (
                  <div className="flex gap-2 flex-wrap">
                    {chargeGroups.traffic.map((charge) => (
                      <div key={charge.id} className="relative group shrink-0">
                         {charge.imageUrl ? (
                           <img
                             src={charge.imageUrl}
                             alt="Traffic"
                             onClick={() => handleImageOpen(charge.imageUrl)}
                             className="h-14 w-14 object-cover rounded border border-gray-300 cursor-pointer hover:shadow-md"
                           />
                         ) : (
                           <div className="h-14 w-14 bg-gray-200 text-[10px] flex items-center justify-center text-center text-gray-500 rounded border">No Img</div>
                         )}
                         <div className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow">
                           ₹{charge.amount}
                         </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No Traffic violations.</p>
                )}
              </div>
              
              {chargeGroups?.damage?.length > 0 && (
    <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
      <h3 className="text-sm font-bold text-purple-800 mb-3">Damage Charges</h3>
      <div className="flex gap-2 flex-wrap">
        {chargeGroups.damage.map((charge) => (
          <div key={charge.id} className="relative group shrink-0">
            {charge.imageUrl ? (
              <img
                src={charge.imageUrl}
                alt="Damage"
                onClick={() => handleImageOpen(charge.imageUrl)}
                className="h-14 w-14 object-cover rounded border border-gray-300 cursor-pointer hover:shadow-md"
              />
            ) : (
              <div className="h-14 w-14 bg-gray-200 text-[10px] flex items-center justify-center text-center text-gray-500 rounded border">
                No Img
              </div>
            )}
            <div className="absolute -top-2 -right-2 bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow">
              ₹{charge.amount}
            </div>
          </div>
        ))}
      </div>
    </div>
  )}

              <div className="bg-green-50 p-4 rounded-xl border border-green-100 flex flex-col justify-center">
                <h3 className="text-sm font-bold text-green-800 mb-2">Extra Usage (Read-Only)</h3>
                <div className="text-xs text-gray-700 space-y-1">
                  <p className="flex justify-between"><span>Extra Kms:</span> <span className="font-bold">{chargeGroups?.extra?.extraKms ?? 0} km</span></p>
                  <p className="flex justify-between text-green-700"><span>Km Charges:</span> <span className="font-bold">₹{chargeGroups?.extra?.extraKmCharges ?? 0}</span></p>
                  <div className="border-t border-green-200 my-1"></div>
                  <p className="flex justify-between"><span>Extra Hrs:</span> <span className="font-bold">{chargeGroups?.extra?.extraHrs ?? 0} hrs</span></p>
                  <p className="flex justify-between text-green-700"><span>Hr Charges:</span> <span className="font-bold">₹{chargeGroups?.extra?.extraHrCharges ?? 0}</span></p>
                </div>
              </div>
            </section>

            {/* Other Charges (Instants Style) */}
            {chargeGroups?.others?.length > 0 && (
              <section>
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Other Charges</h3>
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x hide-scrollbar">
                  {chargeGroups.others.map((charge) => (
                    <div
                      key={charge.id}
                      className="relative shrink-0 w-36 h-48 rounded-xl overflow-hidden shadow-md snap-center cursor-pointer group bg-gray-100 border border-gray-200"
                      onClick={() => handleImageOpen(charge.imageUrl)}
                    >
                      {charge.imageUrl ? (
                        <img
                          src={charge.imageUrl}
                          alt={charge.chargeName}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-200 p-3 text-center">
                          <span className="text-[10px] font-medium">No Image</span>
                        </div>
                      )}
                      <div className="absolute top-0 left-0 w-full h-2/3 bg-gradient-to-b from-black/80 via-black/40 to-transparent p-3 pointer-events-none">
                        <div className="flex flex-col gap-1.5">
                          <span className="text-white text-[11px] font-bold uppercase tracking-wider drop-shadow-lg leading-tight line-clamp-2">
                            {charge.chargeName}
                          </span>
                          <span className="bg-white/20 text-white backdrop-blur-md text-xs font-extrabold px-2.5 py-1 rounded-full w-max shadow-sm border border-white/30">
                            ₹{charge.amount}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Checklist Section */}
<section className="bg-gray-50 p-5 rounded-xl border border-gray-200">
  <div className="flex justify-between items-center border-b pb-3 mb-4">
    <h3 className="text-lg font-semibold text-gray-800">Verification Checklist</h3>
    {!readOnly && (
    <button
      onClick={handleSelectAll}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center gap-2 ${
        selectAll
          ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
          : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'
      }`}
    >
      {selectAll ? (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          Deselect All
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
          Select All
        </>
      )}
    </button>
  )}
  </div>

  <div className="space-y-4">
    {/* Auto-Derived Checks */}
    {derivedChecks?.map((check) => (
      <div
        key={check.id}
        className="p-4 rounded-lg border bg-white shadow-sm space-y-2"
      >
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id={`check-${check.id}`}
            checked={!!checkedStates[check.id]}
            onChange={() => {
              handleCheckboxChange(check.id);
              // Update selectAll state when individual checkbox changes
              const allCheckIds = [
                ...(manualChecks?.map(c => c.id) || []),
                ...(derivedChecks?.map(c => c.id) || [])
              ];
              // Use setTimeout to ensure state is updated
              setTimeout(() => {
                setCheckedStates(prev => {
                  const allChecked = allCheckIds.every(id => prev[id]);
                  setSelectAll(allChecked);
                  return prev;
                });
              }, 0);
            }}
            disabled={readOnly}
            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer shrink-0"
          />
          <label
            htmlFor={`check-${check.id}`}
            className="font-medium text-gray-800 flex-1 select-none cursor-pointer"
          >
            {check.label}
          </label>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 shrink-0">
            Auto-Derived
          </span>
        </div>

        {/* Optional Comment Field */}
        <div className="pl-8 pt-1">
          <input
            type="text"
            disabled={readOnly}
            value={checklistComments[check.id] || ""}
            onChange={(e) => handleCommentChange(check.id, e.target.value)}
            placeholder="Add optional comment for this check..."
            className="w-full text-xs bg-gray-50 border border-gray-200 rounded-md p-2 text-gray-700 focus:bg-white focus:outline-none focus:border-blue-500 transition"
          />
        </div>
      </div>
    ))}

    {/* Manual Checks */}
    {manualChecks?.map((check) => (
      <div
        key={check.id}
        className="p-4 rounded-lg border bg-white shadow-sm space-y-2"
      >
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id={`check-${check.id}`}
            checked={!!checkedStates[check.id]}
            onChange={() => {
            handleCheckboxChange(check.id);
            // Update selectAll state when individual checkbox changes
            const allCheckIds = [
              ...(manualChecks?.map(c => c.id) || []),
              ...(derivedChecks?.map(c => c.id) || [])
            ];
            // Use setTimeout to ensure state is updated
            setTimeout(() => {
              setCheckedStates(prev => {
                const allChecked = allCheckIds.every(id => prev[id]);
                setSelectAll(allChecked);
                return prev;
              });
            }, 0);
          }}
            disabled={readOnly}
            className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer shrink-0"
          />
          <label
            htmlFor={`check-${check.id}`}
            className="font-medium text-gray-800 flex-1 select-none cursor-pointer"
          >
            {check.label}
          </label>
        </div>

        {/* Optional Comment Field */}
        <div className="pl-8 pt-1">
          <input
            type="text"
            disabled={readOnly}
            value={checklistComments[check.id] || ""}
            onChange={(e) => handleCommentChange(check.id, e.target.value)}
            placeholder="Add optional comment for this check..."
            className="w-full text-xs bg-gray-50 border border-gray-200 rounded-md p-2 text-gray-700 focus:bg-white focus:outline-none focus:border-blue-500 transition"
          />
        </div>
      </div>
    ))}
  </div>
</section>
          </div>

          {/* Footer Buttons */}
          <div className="p-4 bg-gray-100 border-t flex justify-end gap-3 shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-lg font-medium text-gray-700 bg-white border shadow-sm hover:bg-gray-50 transition"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>

            {!readOnly && (
              <>
                <button
                  onClick={() => handleSaveChecklist(0)}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-lg font-bold text-gray-700 bg-gray-200 border border-gray-300 shadow-sm hover:bg-gray-300 transition"
                >
                  {saving ? "Saving..." : "Save as Draft"}
                </button>

                <button
                  onClick={() => handleSaveChecklist(1)}
                  disabled={!isConfirmEnabled() || saving}
                  className={`px-8 py-2.5 rounded-lg font-bold text-white shadow-md transition-all ${
                    isConfirmEnabled() && !saving
                      ? "bg-blue-600 hover:bg-blue-700 active:scale-95"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  {saving ? "Submitting..." : "Confirm Refund"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundChecklistModal;